import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Mui Components
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { CircularProgress } from '@mui/material';

import Button from '@mui/material/Button';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useDispatch } from 'react-redux';
// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';
// Utils
import request from '~/utils/request';
import { MESSAGES } from '~/utils/message';
import { downloadFile, numberWithComma } from '~/utils/helperFunctions';
// Components
import ConfirmModal from '~/components/General/ConfirmModal';
// Icons
import { GreenTickCIcon } from '~/assets/images';
// styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';

const ProgressBar = (props) => {
  const {
    onBackToMap,
    value = 0,
    requestId,
    processCsv,
    selectedTagIds = [],
    isDncList = false,
    isOrdersContext = false,
    onProgressStateChange,
    triggerImportSignal,
    triggerBackToMapSignal,
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [downloadProcessedResult, setDownloadProcessedResult] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const showProcessedResultDownload = Boolean(processCsv?.resultsCsvFile);
  const invalidCustomFieldsCount = processCsv?.invalidCustomFields || 0;
  const hasSkippedOrInvalidRows =
    (processCsv?.error || 0) > 0 ||
    (processCsv?.duplicate || 0) > 0 ||
    invalidCustomFieldsCount > 0;

  const handleBackToMap = () => {
    if (onBackToMap) {
      onBackToMap();
    }
  };

  const handleDownloadProcessedResult = async () => {
    setDownloadProcessedResult(true);
    await downloadFile(processCsv.resultsCsvFile, 'processed-result.csv');
    setDownloadProcessedResult(false);
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      // Clear any previous footer error before attempting import
      if (typeof onProgressStateChange === 'function') {
        onProgressStateChange({
          canImport: value >= 100 && !!processCsv && !processCsv.processing,
          total: processCsv?.total || 0,
          isProcessing: !!processCsv?.processing,
          value,
          errorMessage: '',
        });
      }
      const response = await request.post('contacts/import', {
        reqId: requestId,
        errorRecordCount: processCsv?.error || 0,
        isDncList: isDncList,
        tagIds: selectedTagIds.join(','), // Include selected tag IDs from props
      });
      dispatch(success(response.data.message || MESSAGES.CONTACTS.IMPORT.SUCCESS));
      // In Orders context, proceed to Mailer Details instead of navigating away
      if (isOrdersContext && typeof onProgressStateChange === 'function') {
        onProgressStateChange({
          canImport: false,
          total: processCsv?.total || 0,
          isProcessing: false,
          value: 100,
          errorMessage: '',
          importCompleted: true,
        });
      } else {
        navigate(
          window?.location?.search?.includes('origin=contacts')
            ? '/contacts'
            : '/account/imports-exports'
        );
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
      // Report error to parent OrderFlow footer
      if (typeof onProgressStateChange === 'function') {
        onProgressStateChange({
          canImport: value >= 100 && !!processCsv && !processCsv.processing,
          total: processCsv?.total || 0,
          isProcessing: !!processCsv?.processing,
          value,
          errorMessage: error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR,
        });
      }
      return error;
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = async () => {
    try {
      setCancelLoading(true);
      if (requestId) {
        const removeFile = await request.delete(`contacts/import/${requestId}/delete`);
        dispatch(success(removeFile?.data?.message || 'Import cancelled'));
      }
      setIsCancelModalOpen(false);
      navigate('/account/imports-exports');
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setCancelLoading(false);
    }
  };

  // Report progress state up to parent (for main footer binding)
  useEffect(() => {
    const canImport = value >= 100 && !!processCsv && !processCsv.processing;
    const total = processCsv?.total || 0;
    const isProcessing = !!processCsv?.processing;
    if (typeof onProgressStateChange === 'function') {
      onProgressStateChange({ canImport, total, isProcessing, value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, processCsv]);

  // Handle triggers from parent footer
  const lastImportSignalRef = React.useRef(triggerImportSignal);
  const lastBackToMapSignalRef = React.useRef(triggerBackToMapSignal);
  useEffect(() => {
    if (triggerImportSignal !== undefined && triggerImportSignal !== lastImportSignalRef.current) {
      lastImportSignalRef.current = triggerImportSignal;
      const canImportNow = value >= 100 && !!processCsv && !processCsv.processing && !loading;
      if (canImportNow) {
        handleImport();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerImportSignal]);
  useEffect(() => {
    if (
      triggerBackToMapSignal !== undefined &&
      triggerBackToMapSignal !== lastBackToMapSignalRef.current
    ) {
      lastBackToMapSignalRef.current = triggerBackToMapSignal;
      handleBackToMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerBackToMapSignal]);

  return (
    <>
      <Box
        className="dragDropContainer importFinishStepContainer"
        sx={{ py: 0, px: 2, display: 'flex', flexDirection: 'column' }}
      >
        <div
          className="cpProgressBarContainer"
          style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
        >
          <Box
            sx={{
              background: 'rgba(249, 250, 251, 1)',
              padding: '40px',
              borderRadius: '12px',
              marginBottom: '16px',
              minHeight: !processCsv.processing ? '211px' : 'auto',
              height: 'auto',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              sx={{
                fontWeight: '600',
                size: '16px',
                marginBottom: '16px',
                color: 'rgba(0, 0, 0, 1)',
                lineHeight: 'normal',
                height: '16px',
              }}
            >
              File Details
            </Typography>
            <Box className="progressBarWrapper" sx={{ marginBottom: '16px' }}>
              <Box
                sx={{
                  width: {
                    xs: '100%',
                    sm: '460px',
                  },
                  maxWidth: '100%',
                  mr: 1,
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={value}
                  sx={{
                    height: '8.5px !important',
                    backgroundColor: 'rgba(217, 217, 217, 1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#1976d2',
                    },
                  }}
                />
              </Box>
              <Box className="progressValue" sx={{ minWidth: 35 }}>
                <Typography>
                  {`${Math.round(value)}%`} {Math.round(value) >= 100 ? '' : '(Processing)'}
                </Typography>
              </Box>
            </Box>

            {processCsv && !processCsv.processing && (
              <>
                <Box>
                  <Typography className="qtyText">
                    {numberWithComma(processCsv.imported)}
                    {processCsv.imported <= 1
                      ? MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.VALID_CONTACT
                      : MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.VALID_CONTACTS}
                  </Typography>
                  <Box className="mappingError">
                    {hasSkippedOrInvalidRows && (
                      <Typography>
                        {MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.PROCESSED_RESULT_DOWNLOAD_TEXT}
                      </Typography>
                    )}
                    <Typography>
                      {numberWithComma(processCsv.error)}
                      {processCsv.error <= 1
                        ? MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.MISSING_CONTACT
                        : MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.MISSING_CONTACTS}
                    </Typography>
                    <Typography>
                      {numberWithComma(processCsv.duplicate)}
                      {processCsv.duplicate <= 1
                        ? MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.DUPLICATES_CONTACT
                        : MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.DUPLICATES_CONTACTS}
                    </Typography>
                    {invalidCustomFieldsCount > 0 && (
                      <Typography>
                        {numberWithComma(invalidCustomFieldsCount)}
                        {invalidCustomFieldsCount <= 1
                          ? MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.INVALID_CUSTOM_FIELD_CONTACT
                          : MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS
                              .INVALID_CUSTOM_FIELD_CONTACTS}
                      </Typography>
                    )}
                  </Box>
                  {showProcessedResultDownload && (
                    <Box className="processedResultCard">
                      <Box className="processedResultContent">
                        <Box className="processedResultTitleRow">
                          <img src={GreenTickCIcon} alt="green" />
                          <Typography>
                            {MESSAGES.CONTACTS.IMPORT.SMARTY_VALIDATIONS.PROCESSED_RESULT}
                          </Typography>
                        </Box>
                        <Typography className="processedResultDescription">
                          Processed successfully. Download CSV for details.
                        </Typography>
                      </Box>
                      <Button
                        type="button"
                        variant="outlined"
                        className="processedResultDownloadBtn"
                        onClick={handleDownloadProcessedResult}
                        disabled={downloadProcessedResult}
                        startIcon={
                          downloadProcessedResult ? (
                            <CircularProgress
                              sx={{
                                color: '#239a55',
                                width: '18px !important',
                                height: '18px !important',
                              }}
                            />
                          ) : (
                            <SaveAltIcon />
                          )
                        }
                      >
                        Download Processed CSV
                      </Button>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </div>

        {/* Bottom sticky footer action bar */}
        {!isOrdersContext && (
          <Box className="finishFooter importFlowFooter" style={{ marginTop: 'auto' }}>
            <Box className="footerLeft">
              <Button className="backLink" onClick={handleBackToMap}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="10"
                  viewBox="0 0 14 10"
                  fill="none"
                >
                  <path
                    d="M5 9L1 5M1 5L5 1M1 5L13 5"
                    stroke="black"
                    strokeOpacity="0.6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Back to Mapping
              </Button>
              <div>
                {processCsv && !processCsv.processing && (
                  <Box className="totalContactsFooter">
                    <Typography>
                      Total Contacts:{' '}
                      <span className="boldNum">{numberWithComma(processCsv?.total || 0)}</span>
                    </Typography>
                  </Box>
                )}
              </div>
            </Box>

            <Box className="footerRight">
              <OlcButton
                variant="secondary"
                disabled={value < 100 || !processCsv || processCsv.processing}
                onClick={() => setIsCancelModalOpen(true)}
              >
                Cancel
              </OlcButton>

              <OlcButton
                variant="primary"
                disabled={value < 100 || !processCsv || processCsv.processing}
                onClick={handleImport}
              >
                {loading ? (
                  <CircularProgress
                    sx={{ color: 'white', width: '25px !important', height: '25px !important' }}
                  />
                ) : (
                  'Import'
                )}
              </OlcButton>
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmModal
        openConfirmModal={{
          open: isCancelModalOpen,
          header: 'Leave Import?',
          message:
            'You have unsaved changes. If you leave now, your progress will be lost and no contacts will be imported.',
          component: <WarningAmberOutlinedIcon />,
          containedIcon: true,
          successButtonName: 'Yes',
          loading: cancelLoading,
          cancelButtonName: 'No',
          handleOk: handleCancelImport,
          handleCancelBtn: () => {
            setIsCancelModalOpen(false);
          },
          handleClose: () => {
            setIsCancelModalOpen(false);
          },
        }}
      />
    </>
  );
};

export default ProgressBar;
